import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Alert, AlertDescription, AlertTitle } from "../../../app/components/ui/alert";
import { Button } from "../../../app/components/ui/button";
import { Input } from "../../../app/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../app/components/ui/select";
import { Textarea } from "../../../app/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../app/components/ui/form";
import { api } from "../../../lib/api";
import { applyLaravelValidationErrors, getErrorMessage, getLaravelValidationErrors } from "../../../lib/laravelErrors";
import { useAuth } from "../../../providers/AuthProvider";
import { PlaceAutocompleteInput } from "./PlaceAutocompleteInput";

export type DealerFormValues = {
  name: string;
  status: string;
  contact_person: string;
  phone: string;
  email: string;
  gst_number: string;
  address: string;
  lat?: string;
  lng?: string;
  place_id?: string;
};

export function DealerForm({
  mode,
  dealerId,
  defaultValues,
  onSuccess,
}: {
  mode: "create" | "edit";
  dealerId?: string;
  defaultValues?: Partial<DealerFormValues>;
  onSuccess: () => void;
}) {
  const { token } = useAuth();
  const [rootError, setRootError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [addressSearch, setAddressSearch] = useState(defaultValues?.address ?? "");

  const form = useForm<DealerFormValues>({
    defaultValues: {
      name: defaultValues?.name ?? "",
      status: defaultValues?.status ?? "active",
      contact_person: defaultValues?.contact_person ?? "",
      phone: defaultValues?.phone ?? "",
      email: defaultValues?.email ?? "",
      gst_number: defaultValues?.gst_number ?? "",
      address: defaultValues?.address ?? "",
      lat: defaultValues?.lat ?? "",
      lng: defaultValues?.lng ?? "",
      place_id: defaultValues?.place_id ?? "",
    },
  });

  useEffect(() => {
    setAddressSearch(defaultValues?.address ?? "");
  }, [defaultValues?.address]);

  const watchedName = form.watch("name");
  const watchedStatus = form.watch("status");
  const canSubmit = Boolean(watchedName?.trim() && watchedStatus?.trim());

  async function onSubmit(values: DealerFormValues) {
    if (!token) return;
    setRootError(null);
    setSubmitting(true);
    try {
      const payload = {
        name: values.name.trim(),
        status: values.status.trim(),
        contact_person: values.contact_person.trim() || null,
        phone: values.phone.trim() || null,
        email: values.email.trim() || null,
        gst_number: values.gst_number.trim() || null,
        address: values.address.trim() || null,
        lat: values.lat?.trim() ? values.lat.trim() : null,
        lng: values.lng?.trim() ? values.lng.trim() : null,
        place_id: values.place_id?.trim() ? values.place_id.trim() : null,
      };

      if (mode === "create") {
        await api.dealers.create({ token, payload });
      } else {
        await api.dealers.update({ token, id: dealerId!, payload });
      }

      onSuccess();
    } catch (e) {
      const errors = getLaravelValidationErrors(e);
      if (errors) {
        applyLaravelValidationErrors(form.setError, errors);
        setRootError("Please fix the highlighted fields.");
        return;
      }
      setRootError(getErrorMessage(e, "Failed to save dealer"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4">
      {rootError ? (
        <Alert variant="destructive">
          <AlertTitle>Save failed</AlertTitle>
          <AlertDescription>{rootError}</AlertDescription>
        </Alert>
      ) : null}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="name"
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Dealer name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              rules={{ required: "Status is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="contact_person"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact person</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional" type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="gst_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>GST number</FormLabel>
                  <FormControl>
                    <Input placeholder="Optional" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address</FormLabel>
                <FormControl>
                  <Textarea placeholder="Optional" rows={3} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormItem>
            <FormLabel>Search address</FormLabel>
            <FormControl>
              <PlaceAutocompleteInput
                value={addressSearch}
                onChange={setAddressSearch}
                onPick={(sel) => {
                  setAddressSearch(sel.formattedAddress);
                  form.setValue("address", sel.formattedAddress, { shouldDirty: true });
                  form.setValue("place_id", sel.placeId ?? "", { shouldDirty: true });
                  form.setValue("lat", sel.lat !== null ? String(sel.lat) : "", { shouldDirty: true });
                  form.setValue("lng", sel.lng !== null ? String(sel.lng) : "", { shouldDirty: true });
                }}
              />
            </FormControl>
          </FormItem>

          <div className="flex items-center justify-end gap-2">
            <Button type="submit" disabled={!canSubmit || submitting}>
              {submitting ? "Saving..." : mode === "create" ? "Create dealer" : "Save changes"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

